/**
 * Tout ce qui touche aux montants. Fonctions pures, aucune dependance.
 *
 * ## Pourquoi des centimes entiers
 *
 * `0.1 + 0.2 !== 0.3`. Sur un budget, l'erreur reste invisible des mois, puis
 * ressort au pire endroit : une repartition dont la somme des parts ne retombe
 * pas sur le total de la facture, dans un document remis a un tiers pour lui
 * reclamer de l'argent. Les montants sont donc des entiers de centimes de bout
 * en bout, et ne redeviennent decimaux qu'a l'affichage.
 */

import type { Cents } from './types'

export const CENTIMES_PAR_FRANC = 100

// ---------------------------------------------------------------------------
// Mise en forme
// ---------------------------------------------------------------------------

/**
 * Separateur de milliers : l'apostrophe **droite**.
 *
 * Aucune locale ne la produit telle quelle, d'ou la normalisation plus bas :
 *
 *   `fr-CH` donne `1 234.50 CHF` — espace fine insecable (U+202F), symbole en
 *           suffixe. Ce n'est pas la forme d'un document comptable suisse.
 *   `de-CH` donne `CHF 1’234.50` — la bonne structure, mais avec l'apostrophe
 *           typographique U+2019.
 *
 * On part donc de `de-CH` (structure correcte) et on remplace le separateur de
 * groupe. L'apostrophe droite est la forme retenue par la Chancellerie
 * federale, celle des releves bancaires, et surtout la seule dont on soit
 * certain qu'elle traverse sans dommage l'encodage WinAnsi des polices
 * standard de pdf-lib — le justificatif est precisement le document ou un
 * caractere de remplacement serait le plus genant.
 */
export const SEPARATEUR_MILLIERS = "'"

const FMT_CHF = new Intl.NumberFormat('de-CH', {
  style: 'currency',
  currency: 'CHF',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const FMT_NOMBRE = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Applique les conventions du projet aux morceaux produits par `Intl`. */
function assembler(parts: Intl.NumberFormatPart[]): string {
  return parts
    .map((p) => {
      if (p.type === 'group') return SEPARATEUR_MILLIERS
      // L'espace insecable entre `CHF` et le nombre est conservee : c'est elle
      // qui empeche un retour a la ligne entre le symbole et son montant.
      if (p.type === 'minusSign') return '−' // vrai signe moins, pas un tiret
      return p.value
    })
    .join('')
}

/** `CHF 1'234.50`. La seule facon d'ecrire un montant dans tout le projet. */
export function formatMontant(c: Cents): string {
  // Le signe se place devant le symbole : `−CHF 1'320.00`, symetrique de
  // `+CHF 150.00`. `Intl` le glisserait entre les deux (`CHF-1'320.00`).
  const abs = assembler(FMT_CHF.formatToParts(Math.abs(c) / CENTIMES_PAR_FRANC))
  return c < 0 ? `\u2212${abs}` : abs
}

/** `1'234.50`, sans le symbole. Pour les colonnes de tableau, ou repeter
 *  « CHF » sur chaque ligne ajoute du bruit sans information. */
export function formatNombre(c: Cents): string {
  const abs = assembler(FMT_NOMBRE.formatToParts(Math.abs(c) / CENTIMES_PAR_FRANC))
  return c < 0 ? `\u2212${abs}` : abs
}

/** Force le signe : `+CHF 120.00`, `−CHF 120.00`. Pour les variations et les
 *  mouvements de tresorerie, ou l'absence de signe serait ambigue. */
export function formatMontantSigne(c: Cents): string {
  if (c === 0) return formatMontant(0)
  return c > 0 ? `+${formatMontant(c)}` : formatMontant(c)
}

const FMT_POURCENT = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export function formatPourcentage(p: number): string {
  return `${FMT_POURCENT.format(p)} %`
}

// ---------------------------------------------------------------------------
// Saisie
// ---------------------------------------------------------------------------

/**
 * Lit un montant tape a la main et le convertit en centimes.
 *
 * Accepte tout ce qu'un utilisateur suisse est susceptible d'ecrire :
 * `1'234.50`, `1’234.50`, `1 234.50`, `1234,50`, `1234.5`, `1234`, `CHF 12.-`.
 * La forme `12.-` est courante sur les factures suisses et vaut `12.00`.
 *
 * Renvoie `null` si la chaine n'est pas un montant — jamais `NaN` ni `0`, qui
 * se propageraient silencieusement dans les calculs.
 */
export function parseMontant(saisie: string): Cents | null {
  let s = saisie.trim()
  if (s === '') return null

  s = s
    .replace(/chf/gi, '')
    .replace(/−/g, '-') // signe moins typographique
    .replace(/[''’  \s]/g, '') // separateurs de milliers
    .replace(',', '.')
    .replace(/\.-$/, '.00') // « 12.- » vaut « 12.00 »
    .trim()

  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null

  const [entier = '0', decimales = ''] = s.split('.')
  const negatif = entier.startsWith('-')
  const francs = Math.abs(Number(entier))
  const centimes = Number(decimales.padEnd(2, '0'))
  if (!Number.isFinite(francs) || !Number.isFinite(centimes)) return null

  const total = francs * CENTIMES_PAR_FRANC + centimes
  return negatif ? -total : total
}

/** Valeur pour un `<input type="number" step="0.05">` : `1234.5`. Sans
 *  separateur de milliers, que l'input refuserait. */
export function versSaisie(c: Cents): string {
  return (c / CENTIMES_PAR_FRANC).toFixed(2)
}

// ---------------------------------------------------------------------------
// Arithmetique
// ---------------------------------------------------------------------------

/**
 * Arrondit au centime, **moitie a l'ecart de zero**.
 *
 * `Math.round` arrondit vers +infini : `Math.round(-2.5)` vaut `-2`. Une
 * depense et un remboursement de meme valeur absolue s'arrondiraient donc
 * differemment, et un aller-retour ne retomberait pas sur zero.
 */
export function arrondir(n: number): Cents {
  return n < 0 ? -Math.round(-n) : Math.round(n)
}

export function somme(montants: readonly Cents[]): Cents {
  let t = 0
  for (const m of montants) t += m
  return t
}

/**
 * `pourcent` % de `base`, arrondi au centime.
 *
 * Le calcul passe par des entiers (le pourcentage est porte a quatre
 * decimales) avant l'unique arrondi final. Multiplier des flottants puis
 * arrondir ferait basculer du mauvais cote les valeurs pile a la charniere :
 * `40001 * 0.5` vaut `20000.5` en theorie, mais un cumul d'imprecision peut le
 * rendre `20000.499999…`, et la part perdrait un centime sans raison visible.
 */
export function pourcentageDe(base: Cents, pourcent: number): Cents {
  const pctE4 = Math.round(pourcent * 10_000)
  return arrondir((base * pctE4) / 1_000_000)
}

/** Borne une valeur dans un intervalle. */
export function borner(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max)
}

/**
 * Repartit `total` en `n` parts aussi egales que possible, **sans perdre ni
 * creer de centime**. Les premieres parts recoivent le centime en trop.
 *
 * Sert aux partages simples (« moitie-moitie ») et sert surtout de reference
 * au test qui verifie que la somme retombe toujours sur le total.
 */
export function repartirEgalement(total: Cents, n: number): Cents[] {
  if (n <= 0) return []
  const signe = total < 0 ? -1 : 1
  const abs = Math.abs(total)
  const base = Math.floor(abs / n)
  const reste = abs - base * n
  return Array.from({ length: n }, (_, i) => signe * (base + (i < reste ? 1 : 0)))
}

/**
 * Variation relative entre deux montants, en pourcentage.
 *
 * `null` quand la reference est nulle : passer de 0 a 50 francs n'est pas une
 * hausse de « l'infini pour cent », c'est une comparaison qui n'a pas de sens.
 * Renvoyer `null` force l'appelant a l'afficher autrement plutot qu'a propager
 * un `Infinity` dans un graphique.
 */
export function variation(actuel: Cents, reference: Cents): number | null {
  if (reference === 0) return null
  return ((actuel - reference) / Math.abs(reference)) * 100
}
